/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import Styles from '../../css/LoanInformation.module.css';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { formatDateForInputWithOffset, getTimestampAsDate, getLpiDueDateString } from '../../common/UtilityFunctions';
import { NumericFormat } from 'react-number-format';
import { useLoanContext } from '../../contexts/LoanContext';
import { useMemo } from 'react';
import useGetPaymentInfo from '../../common/hooks/useGetPaymentInfo';
import useGetServicingInstallments from '../../common/hooks/useGetServicingInstallments';
import useLateCharge from '../../common/hooks/useLateChargeData';

const NextPaymentDueInfo = () => {
  const { loanNumber } = useLoanContext();
  const { loanData, loanEvents, handleLoanEventChange } = useLoanInformationContext();
  const dueDateString = getLpiDueDateString(loanData?.lpi);
  const { paymentInfo } = useGetPaymentInfo(loanNumber, dueDateString);
  const { servInstData: servicingInstallmentsInfo } = useGetServicingInstallments(loanNumber, dueDateString);
  const { servInstData: upcomingInstallmentsInfo, reloadServInstData } = useGetServicingInstallments(
    loanNumber,
    'next-change',
  );

  const nextPaymentValid = useMemo(() => {
    return loanData?.servicingState === '1' && !servicingInstallmentsInfo?.errors;
  }, [loanData?.servicingState, servicingInstallmentsInfo]);

  const upcomingPaymentValid = useMemo(() => {
    return (
      loanData?.servicingState === '1' &&
      !upcomingInstallmentsInfo?.errors &&
      upcomingInstallmentsInfo?.dueDate !== null &&
      upcomingInstallmentsInfo?.dueDate !== 0
    );
  }, [loanData?.servicingState, servicingInstallmentsInfo]);

  const { lateChargeData } = useLateCharge();

  return (
    <EnvelopeWithBox blockName='Next Payment Due Information'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <h3 className={Styles.headerTitles}>Next Payment Due</h3>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Due Date:</th>
                <td>
                  {paymentInfo?.dueDate != null && loanData?.servicingState === '1'
                    ? getTimestampAsDate(paymentInfo?.dueDate)
                    : 'TBD'}
                </td>
              </tr>
              <tr>
                <th>Principal & Interest: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={nextPaymentValid ? servicingInstallmentsInfo?.pi || 0 : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Escrow (Taxes & Insurance): </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={nextPaymentValid ? servicingInstallmentsInfo?.ti || 0 : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              {servicingInstallmentsInfo?.prinSubsidy || servicingInstallmentsInfo?.intSubsidy ? (
                <tr>
                  <th>Total Subsidies: </th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={
                        nextPaymentValid
                          ? (servicingInstallmentsInfo?.prinSubsidy || 0) + (servicingInstallmentsInfo?.intSubsidy || 0)
                          : 0
                      }
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
              ) : null}
              <tr>
                <th>Total Payment Due: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={nextPaymentValid ? servicingInstallmentsInfo?.borrowerPiti || 0 : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>One Time Late Charge: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={nextPaymentValid ? lateChargeData?.lateFee || 0 : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <h3 className={Styles.headerTitles}>Upcoming Payment Change Info</h3>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Pending Due Date: </th>
                <td>
                  {upcomingPaymentValid &&
                  upcomingInstallmentsInfo?.changeType === 'AEA' &&
                  loanEvents?.AEA?.activeDate === upcomingInstallmentsInfo?.dueDate ? (
                    <input
                      type='date'
                      className={Styles.calicon}
                      name='date'
                      value={
                        loanEvents?.AEA?.activeDate ? formatDateForInputWithOffset(loanEvents?.AEA?.activeDate) : 'TBD'
                      }
                      onChange={(e) => {
                        const [year, month, day] = e.target.value.split('-');
                        const newAeaDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        const timestamp = Math.floor(newAeaDate.getTime() / 1000);
                        handleLoanEventChange('edit', 'AEA.activeDate', timestamp);
                      }}
                    />
                  ) : (
                    getTimestampAsDate(upcomingInstallmentsInfo?.dueDate) || 'TBD'
                  )}
                </td>
              </tr>
              <tr>
                <th>Upcoming Change Type</th>
                <td>
                  {upcomingInstallmentsInfo?.dueDate && loanData?.servicingState === '1'
                    ? upcomingInstallmentsInfo?.errors
                      ? 'Data TBD'
                      : upcomingInstallmentsInfo?.changeType
                    : ''}
                </td>
              </tr>
              <tr>
                <th>Pending Principal & Interest: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={upcomingPaymentValid ? upcomingInstallmentsInfo?.pi || '' : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Pending Escrow (Taxes & Insurance): </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={upcomingPaymentValid ? upcomingInstallmentsInfo?.ti || '' : ''}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              {upcomingInstallmentsInfo?.prinSubsidy || upcomingInstallmentsInfo?.intSubsidy ? (
                <tr>
                  <th>Total Subsidies:</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={
                        upcomingPaymentValid
                          ? (upcomingInstallmentsInfo?.prinSubsidy || 0) + (upcomingInstallmentsInfo?.intSubsidy || 0)
                          : ''
                      }
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
              ) : null}
              <tr>
                <th>Pending Payment Amount: </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={
                      upcomingPaymentValid && (upcomingInstallmentsInfo?.pi || upcomingInstallmentsInfo?.ti)
                        ? (upcomingInstallmentsInfo?.pi || 0) + (upcomingInstallmentsInfo?.ti || 0)
                        : ''
                    }
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default NextPaymentDueInfo;
